// Modal "Mover caja" — sheet con lista de palets disponibles + opción
// "Sin palet". Devuelve `palet_sscc | null | undefined`:
//   - palet_sscc (string) → mover a ese palet
//   - null → quitar del palet (dejar suelta)
//   - undefined → cancelar
//
// La API real de mover (POST /cajas/{sscc}/mover) la llama el caller, este
// modal solo se ocupa de la selección.

import { icon } from './icons.js';

const escapeHtml = (s) => String(s ?? '').replace(/[&<>"']/g,
  c => ({ '&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;',"'":'&#39;' }[c]));

let _host = null;

function _close() { if (_host) { _host.remove(); _host = null; } }

/**
 * @param {object} opts
 * @param {{sscc:string}} opts.caja  — caja que se está moviendo (para mostrar SSCC)
 * @param {Array<{sscc:string, numero:number, cajas:any[]}>} opts.palets  — palets disponibles
 * @param {string|null} opts.currentPaletSscc  — palet actual de la caja (o null si suelta)
 */
export function openMoverCajaModal({ caja, palets, currentPaletSscc }) {
  return new Promise((resolve) => {
    _host = document.createElement('div');
    document.body.appendChild(_host);

    const opciones = palets.map(p => {
      const isCurrent = p.sscc === currentPaletSscc;
      const last8 = p.sscc.slice(-8);
      return `
        <button class="mc-opt" data-target="${escapeHtml(p.sscc)}" ${isCurrent ? 'data-current="1"' : ''}
                style="
                  display:flex; align-items:center; gap:0.6rem; width:100%;
                  padding: 0.85rem 1rem;
                  background: ${isCurrent ? 'var(--c-pending-bg)' : 'var(--c-surface)'};
                  border: 1px solid ${isCurrent ? 'var(--c-border-strong)' : 'var(--c-border)'};
                  border-left: 5px solid var(--c-accent);
                  border-radius: var(--radius);
                  text-align: left; cursor: ${isCurrent ? 'default' : 'pointer'};
                  opacity: ${isCurrent ? '0.6' : '1'};
                ">
          <span style="display:inline-flex;color:var(--c-accent);">${icon('layers', { size: 22 })}</span>
          <span style="flex:1;">
            <strong>Palet #${p.numero}</strong>
            <span style="display:block;color:var(--c-fg-muted);font-size:var(--fs-xs);font-family:ui-monospace,monospace;">…${last8}</span>
            <span style="color:var(--c-fg-muted);font-size:var(--fs-xs);">${p.cajas.length} ${p.cajas.length === 1 ? 'caja ya' : 'cajas ya'} dentro</span>
          </span>
          ${isCurrent ? `<span style="color:var(--c-fg-muted);font-size:var(--fs-xs);">aquí está</span>` : ''}
        </button>`;
    }).join('');

    const opcionSinPalet = currentPaletSscc === null
      ? '' // ya está suelta — no la ofrezco
      : `<button class="mc-opt" data-target=""
                 style="
                   display:flex; align-items:center; gap:0.6rem; width:100%;
                   padding: 0.85rem 1rem;
                   background: var(--c-surface);
                   border: 1px dashed var(--c-border-strong);
                   border-radius: var(--radius);
                   text-align: left; cursor: pointer;
                 ">
          <span style="display:inline-flex;color:var(--c-fg-muted);">${icon('package', { size: 22 })}</span>
          <span style="flex:1;">
            <strong>Sin palet</strong>
            <span style="display:block;color:var(--c-fg-muted);font-size:var(--fs-xs);">Sacar la caja del palet actual y dejarla suelta</span>
          </span>
        </button>`;

    _host.innerHTML = `
      <div class="scrim fade-in"><div class="modal">
        <h2>Mover caja</h2>
        <p style="margin:0;color:var(--c-fg-muted);font-size:var(--fs-sm);">
          SSCC <span style="font-family:ui-monospace,monospace;">…${caja.sscc.slice(-8)}</span>
        </p>
        ${palets.length === 0 && currentPaletSscc === null
          ? `<div class="lote-missing" style="margin-top:0.5rem;">
                <span style="display:inline-flex;">${icon('alert-triangle', { size: 18 })}</span>
                <span>No hay palets en este pedido todavía. Crea uno desde la pantalla de empaquetado.</span>
              </div>`
          : `<div style="display:flex;flex-direction:column;gap:0.5rem;max-height:60dvh;overflow-y:auto;">
              ${opciones}
              ${opcionSinPalet}
            </div>`}
        <div class="actions">
          <button class="btn --ghost --block" id="mc-cancel">Cancelar</button>
        </div>
      </div></div>`;

    _host.querySelector('#mc-cancel').addEventListener('click', () => { _close(); resolve(undefined); });

    _host.querySelectorAll('.mc-opt').forEach(btn => {
      if (btn.dataset.current) return;  // botón del palet actual = no interactivo
      btn.addEventListener('click', () => {
        const t = btn.dataset.target;
        _close();
        resolve(t === '' ? null : t);  // '' = sin palet, sscc = mover ahí
      });
    });
  });
}
